import type { ITicketFeature } from '@/shared/api/types/ticket-feature-types'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  useCreateTicketFeatureMutation,
  useUpdateTicketFeatureMutation,
} from '@/entities/ticket-feature'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/shared/ui/sheet'
import type { TicketFeatureFormValues } from './schema'
import { Textarea } from '@/shared/ui/textarea'
import { EMPTY_TICKET_FEATURE_FORM_VALUES, ticketFeatureFormSchema } from './schema'

interface TicketFeatureDrawerProps {
  open: boolean
  feature?: ITicketFeature | null
  onOpenChange: (open: boolean) => void
}

export function TicketFeatureDrawer({ open, feature, onOpenChange }: TicketFeatureDrawerProps) {
  const { t } = useTranslation()
  const isEdit = Boolean(feature)
  const createMutation = useCreateTicketFeatureMutation()
  const updateMutation = useUpdateTicketFeatureMutation()
  const isPending = createMutation.isPending || updateMutation.isPending

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TicketFeatureFormValues>({
    resolver: zodResolver(ticketFeatureFormSchema),
    defaultValues: EMPTY_TICKET_FEATURE_FORM_VALUES,
  })

  useEffect(() => {
    if (open) {
      if (feature) {
        reset({
          featureKey: feature.featureKey,
          localizedData: {
            en: {
              name: feature.localizedData?.['en']?.name ?? '',
              description: feature.localizedData?.['en']?.description ?? '',
            },
            'vi-south': {
              name: feature.localizedData?.['vi-south']?.name ?? '',
              description: feature.localizedData?.['vi-south']?.description ?? '',
            },
          },
        })
      } else {
        reset(EMPTY_TICKET_FEATURE_FORM_VALUES)
      }
    }
  }, [open, feature, reset])

  async function onSubmit(values: TicketFeatureFormValues) {
    const localizedData = {
      en: {
        name: values.localizedData.en.name.trim(),
        description: values.localizedData.en.description?.trim() ?? '',
      },
      'vi-south': {
        name: values.localizedData['vi-south'].name.trim(),
        description: values.localizedData['vi-south'].description?.trim() ?? '',
      },
    }
    try {
      if (isEdit && feature) {
        await updateMutation.mutateAsync({ id: feature.id, payload: { localizedData } })
        showToast.success(t('ticketFeature.updateSuccess', { defaultValue: 'Feature updated' }))
      } else {
        await createMutation.mutateAsync({
          featureKey: values.featureKey.trim(),
          localizedData,
        })
        showToast.success(t('ticketFeature.createSuccess', { defaultValue: 'Feature created' }))
      }
      onOpenChange(false)
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 overflow-y-auto sm:max-w-md">
        <SheetHeader className="pb-4">
          <SheetTitle>
            {isEdit
              ? t('ticketFeature.edit', { defaultValue: 'Edit Feature' })
              : t('ticketFeature.create', { defaultValue: 'Create Feature' })}
          </SheetTitle>
        </SheetHeader>

        <form
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e)
          }}
          className="flex flex-1 flex-col gap-4"
        >
          <FormField
            label={t('ticketFeature.featureKey', { defaultValue: 'Feature Key' })}
            required
            error={errors.featureKey?.message ? t(errors.featureKey.message) : undefined}
          >
            <Input {...register('featureKey')} disabled={isEdit} placeholder="e.g. audio_guide" />
          </FormField>

          <p className="text-sm font-medium text-zinc-700">English</p>

          <FormField
            label={t('common.name')}
            required
            error={
              errors.localizedData?.en?.name?.message
                ? t(errors.localizedData.en.name.message)
                : undefined
            }
          >
            <Input {...register('localizedData.en.name')} />
          </FormField>

          <FormField label={t('common.description', { defaultValue: 'Description' })} optional>
            <Textarea {...register('localizedData.en.description')} rows={2} />
          </FormField>

          <p className="text-sm font-medium text-zinc-700">Tiếng Việt</p>

          <FormField
            label={t('common.name')}
            required
            error={
              errors.localizedData?.['vi-south']?.name?.message
                ? t(errors.localizedData['vi-south'].name.message)
                : undefined
            }
          >
            <Input {...register('localizedData.vi-south.name')} />
          </FormField>

          <FormField label={t('common.description', { defaultValue: 'Description' })} optional>
            <Textarea {...register('localizedData.vi-south.description')} rows={2} />
          </FormField>

          <SheetFooter className="mt-auto pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
